import {ExclamationCircleIcon} from '@heroicons/react/outline';
import {CheckCircleIcon} from '@heroicons/react/solid';
import React from 'react';

import {
  POOL_PAYMENT_SENT_AT_MESSAGES,
  validatePoolPaymentSentAt,
} from '@afriex/core/utils';

import {AmountInput, Button, CountryPicker, Input} from '@/components';
import Datepicker from '@/components/DatePicker';
import type {UserModalProps} from '@/components/DeactivateAccount/types';
import Modal from '@/components/Modals';
import type {AfriexCountryCode} from '@/types';
import {AfriexCurrencyCode, CountryStatus} from '@/types';
import {countryCodeToCurrencyCodeMap} from '@/utils/countryCodeToCurrencyCodeMap';
import {logRejection} from '@/utils/logRejection';

import PoolDepositDuplicateBanner from './PoolDepositDuplicateBanner';
import type {SenderDetails} from './types';
import useUploadPoolTransaction from './useUploadPoolTransaction';

type UploadPoolTransactionModalProps = Pick<
  UserModalProps,
  'isOpen' | 'onClose'
>;

const UploadPoolTransactionModal = ({
  isOpen,
  onClose,
}: UploadPoolTransactionModalProps) => {
  const {
    formData,
    setFormData,
    isLoading,
    isValid,
    handleSubmit,
    referenceLookup,
    duplicateWarning,
    confirmSubmitDuplicate,
    dismissDuplicateWarning,
  } = useUploadPoolTransaction(onClose);

  const fixedCurrency = formData.country
    ? (countryCodeToCurrencyCodeMap[formData.country] as AfriexCurrencyCode)
    : undefined;

  const isResolving = referenceLookup.status === 'loading';
  const paymentSentAtIssue = validatePoolPaymentSentAt(formData.paymentSentAt);
  const primaryName =
    referenceLookup.businessName || referenceLookup.customerName;
  const secondaryName = referenceLookup.businessName
    ? referenceLookup.customerName
    : undefined;

  const updateSenderDetail = <K extends keyof SenderDetails>(
    key: K,
    value: SenderDetails[K],
  ) => {
    setFormData((prev) => ({
      ...prev,
      senderDetails: {
        name: '',
        accountNumber: '',
        bankName: '',
        ...prev.senderDetails,
        [key]: value,
      },
    }));
  };

  return (
    <React.Fragment>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        width="w-11/12 sm:w-5/12 md:w-5/12 lg:w-5/12 xl:w-3/12 px-4 sm:px-0"
        closeOnOutsideClick
        title="Upload Pool Transaction"
        dataCy="pool-upload-modal"
      >
        <div className="pt-3 px-2 overflow-y-hidden">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              // `handleSubmit` toasts its own failures (or raises the duplicate
              // banner), so this only fires if that handler itself throws. Log
              // rather than swallow silently.
              handleSubmit().catch(
                logRejection('Failed to confirm pool deposit'),
              );
            }}
            className="flex flex-col"
          >
            {duplicateWarning && (
              <PoolDepositDuplicateBanner
                warning={duplicateWarning}
                isLoading={isLoading}
                onDismiss={dismissDuplicateWarning}
                onConfirm={confirmSubmitDuplicate}
                confirmLabel="Confirm anyway (separate transfer)"
                testIdPrefix="pool-upload"
              >
                A matching pool deposit already exists for this reference and
                amount (transaction {duplicateWarning.existingTransactionId},
                status {duplicateWarning.existingStatus}). Confirming again will
                credit the wallet a second time.
              </PoolDepositDuplicateBanner>
            )}

            <Input
              label="Reference number"
              placeholder="e.g. 64a1b2c3d4e5..."
              value={formData.reference || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFormData((prev) => ({
                  ...prev,
                  reference: e.target.value?.toLowerCase(),
                }))
              }
              className="w-full mb-2"
              data-cy="pool-upload-reference"
            />

            {referenceLookup.status !== 'idle' && (
              <div className="mb-3 mt-1">
                {referenceLookup.status === 'loading' && (
                  <div className="flex items-center gap-2 rounded-lg border border-cyan-500/60 bg-cyan-500/10 px-3 py-2">
                    <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-cyan-600 border-t-transparent" />
                    <span className="text-xs font-medium text-cyan-600 dark:text-cyan-500">
                      Resolving...
                    </span>
                  </div>
                )}
                {referenceLookup.status === 'success' && (
                  <div
                    className="flex items-center justify-between gap-2 rounded-lg border border-cyan-500/60 bg-cyan-500/10 px-3 py-2"
                    data-cy="pool-upload-reference-resolved"
                  >
                    <div className="flex min-w-0 flex-col">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Reference resolved to
                      </span>
                      <span className="break-words font-semibold text-cyan-600 dark:text-cyan-500">
                        {primaryName}
                      </span>
                      {secondaryName && (
                        <span className="break-words text-xs text-gray-500 dark:text-gray-400">
                          {secondaryName}
                        </span>
                      )}
                    </div>
                    <CheckCircleIcon className="h-5 w-5 shrink-0 text-cyan-600 dark:text-cyan-500" />
                  </div>
                )}
                {(referenceLookup.status === 'not-found' ||
                  referenceLookup.status === 'error') && (
                  <div className="flex items-center gap-2 rounded-lg border border-red-500/60 bg-red-500/10 px-3 py-2">
                    <ExclamationCircleIcon className="h-5 w-5 shrink-0 text-red-500" />
                    <span className="text-xs font-medium text-red-600 dark:text-red-400">
                      {referenceLookup.status === 'not-found'
                        ? 'No business or customer found for this reference.'
                        : 'Could not resolve reference. Please try again.'}
                    </span>
                  </div>
                )}
              </div>
            )}

            <fieldset
              disabled={isResolving}
              className={`m-0 flex min-w-0 flex-col border-0 p-0 transition-opacity ${
                isResolving ? 'opacity-50' : ''
              }`}
            >
              <CountryPicker
                label="Country"
                filter={{status: CountryStatus.ACTIVE}}
                value={formData.country || ''}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setFormData((prev) => ({
                    ...prev,
                    country: e.target.value as AfriexCountryCode,
                  }))
                }
                placeholder="--Select Country--"
                className="mb-2"
                dataCy="pool-upload-country"
              />

              <AmountInput
                dataCy="pool-upload-amount"
                label="Amount"
                fixedCurrency={fixedCurrency}
                value={{
                  amount: formData.amount ?? 0,
                  currency: fixedCurrency ?? AfriexCurrencyCode.NGN,
                }}
                onChange={({amount}) =>
                  setFormData((prev) => ({...prev, amount}))
                }
                containerStyle="w-full mb-2"
              />

              <Datepicker
                label="Payment sent at"
                selectedDate={formData.paymentSentAt}
                setSelectedDate={(date: Date) =>
                  setFormData((prev) => ({...prev, paymentSentAt: date}))
                }
                showTime
                // Every minute, not the default 5: rounding 09:07 to 09:05
                // would falsify a time read off a bank record.
                timeIntervals={1}
                maxDate={new Date()}
                containerStyle="w-full mb-2"
              />
              {/* Same rule and wording as the business portal, so the operator
                  sees why Confirm is disabled rather than a dead button. */}
              {paymentSentAtIssue && (
                <p
                  data-testid="payment-sent-at-error"
                  className="mb-2 text-xs font-medium text-red-600 dark:text-red-400"
                >
                  {POOL_PAYMENT_SENT_AT_MESSAGES[paymentSentAtIssue]}
                </p>
              )}

              <div className="border-t border-gray-200 dark:border-dark-borderHeavy my-3 pt-3">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                  Sender Details
                </p>

                <Input
                  label="Sender name"
                  placeholder="e.g. John Doe"
                  value={formData.senderDetails?.name || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateSenderDetail('name', e.target.value)
                  }
                  className="w-full mb-2"
                  data-cy="pool-upload-sender-name"
                />

                <Input
                  label="Sender account number / wallet identifier"
                  placeholder="e.g. 0012345678"
                  value={formData.senderDetails?.accountNumber || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateSenderDetail('accountNumber', e.target.value)
                  }
                  className="w-full mb-2"
                  data-cy="pool-upload-sender-account-number"
                />

                <Input
                  label="Sender bank / financial institution"
                  placeholder="e.g. First Bank"
                  value={formData.senderDetails?.bankName || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateSenderDetail('bankName', e.target.value)
                  }
                  className="w-full mb-2"
                  data-cy="pool-upload-sender-bank"
                />

                <CountryPicker
                  label="Sender country"
                  filter={{status: CountryStatus.ACTIVE}}
                  value={formData.senderDetails?.countryCode || ''}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    updateSenderDetail(
                      'countryCode',
                      e.target.value as AfriexCountryCode,
                    )
                  }
                  placeholder="--Select Country--"
                  className="mb-2"
                />
              </div>
            </fieldset>

            <div className="flex justify-end gap-2 pb-5 mt-5">
              <Button
                type="button"
                variant="ghost"
                colorScheme="cyan"
                onClick={onClose}
                data-cy="pool-upload-cancel"
              >
                Cancel
              </Button>
              <Button
                // Disabled once a duplicate is flagged: re-submitting would
                // just re-hit the same 409, and the banner's own "Confirm
                // anyway" is the only live way forward.
                disabled={
                  !isValid || isLoading || isResolving || !!duplicateWarning
                }
                type="submit"
                variant="solid"
                colorScheme="cyan"
                data-cy="pool-upload-confirm"
              >
                {isLoading ? 'Confirming...' : 'Confirm'}
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </React.Fragment>
  );
};

export default UploadPoolTransactionModal;
